import type { LiveDrawStage } from './resolveLiveDrawStage';

type DrawnNumbersSource = {
  numbers: readonly unknown[];
  extraNumbers?: readonly unknown[];
};

type DrawnNumbers = {
  numbers: number[];
  extraNumbers?: number[];
};

function toNumberList(values: readonly unknown[] | undefined): number[] {
  return values?.map(Number) ?? [];
}

/** Main balls plus any bonus/supplementary set — 0 until the draw has been resulted. */
function getDrawnNumberCount(source: DrawnNumbersSource | null | undefined): number {
  if (!source || !Array.isArray(source.numbers)) {
    return 0;
  }

  return source.numbers.length + (source.extraNumbers?.length ?? 0);
}

/**
 * How many balls should be treated as landed for the current stage.
 *
 * `drawing_numbers` carries the count; the terminal stages expose the whole set so matching and the
 * completed slip do not have to special-case "reveal finished". Everything before the reveal is 0.
 */
function getRevealedCountForStage(stage: LiveDrawStage, drawnNumberCount: number): number {
  const { slot } = stage;

  if (slot.kind === 'drawing_numbers') {
    return Math.min(Math.max(slot.revealedCount, 0), drawnNumberCount);
  }

  if (slot.kind === 'announce_win' || slot.kind === 'calculating_win' || slot.kind === 'complete') {
    return drawnNumberCount;
  }

  return 0;
}

/**
 * Prefix of the payload the live-draw UI may show: main balls first, then extras, up to `revealedCount`.
 *
 * The API returns the whole set at once; this is the only slice consumers should match or render as
 * "already drawn".
 */
function getRevealedNumbers(source: DrawnNumbersSource | null | undefined, revealedCount: number): DrawnNumbers {
  if (!source || revealedCount <= 0) {
    return source?.extraNumbers ? { numbers: [], extraNumbers: [] } : { numbers: [] };
  }

  const numbers = toNumberList(source.numbers).slice(0, revealedCount);

  if (!source.extraNumbers) {
    return { numbers };
  }

  const extraRevealedCount = Math.max(0, revealedCount - source.numbers.length);

  return { numbers, extraNumbers: toNumberList(source.extraNumbers).slice(0, extraRevealedCount) };
}

export { getDrawnNumberCount, getRevealedCountForStage, getRevealedNumbers, toNumberList };
export type { DrawnNumbers, DrawnNumbersSource };
